"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { OfferCard, type OfferType } from "@/components/IKTrendingOffers";
import { fetchOffers } from "@/lib/offers-api";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);

const IKFooter = dynamic(
  () => import("@/components/IKFooter").then((m) => m.IKFooter),
  { ssr: true }
);

const ITEMS_PER_PAGE = 8;

function getPageNumbers(current: number, total: number) {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  if (current <= 3) {
    return [1, 2, 3, 4, "...", total];
  }
  if (current >= total - 2) {
    return [1, "...", total - 3, total - 2, total - 1, total];
  }
  return [1, "...", current - 1, current, current + 1, "...", total];
}

export default function OffersPage() {
  const [offers, setOffers] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let active = true;
    fetchOffers().then((data) => {
      if (active) {
        setOffers(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const totalPages = Math.ceil(offers.length / ITEMS_PER_PAGE) || 1;

  // Get index boundaries for current page
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentOffers = offers.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Smooth scroll to top of page content on page change
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Breadcrumbs */}
          <nav className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-6 font-medium" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0" aria-hidden />
            <span className="text-[#131222] dark:text-white font-semibold">
              Offers
            </span>
          </nav>

          {/* Heading */}
          <div className="mb-8 md:mb-10">
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#131222] dark:text-white tracking-tight mb-3">
              Exclusive <span className="text-[#FFB51D]">Offers</span>
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base max-w-2xl">
              Save more on every ride with our latest discounts and promotional offers for Cars and Bikes.
            </p>
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-lg flex flex-col h-full animate-pulse"
                >
                  <div className="h-40 bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                  <div className="p-5 flex flex-col items-center justify-between">
                    <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-3/4 mb-3 animate-pulse" />
                    <div className="w-full mb-4 space-y-1.5 flex flex-col items-center">
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-5/6 animate-pulse" />
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-2/3 animate-pulse" />
                    </div>
                    <div className="h-[52px] bg-neutral-100 dark:bg-neutral-800/40 border-2 border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg w-full animate-pulse" />
                  </div>
                </div>
              ))
            ) : offers.length === 0 ? (
              <div className="col-span-full py-16 text-center">
                <h2 className="text-2xl font-bold text-[#131222] dark:text-white mb-2">No Active Offers Available</h2>
                <p className="text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">We currently don't have any active discount offers. Please check back later!</p>
              </div>
            ) : (
              currentOffers.map((offer, idx) => (
                <OfferCard key={offer.id} offer={offer} index={idx} />
              ))
            )}
          </div>
        </div>

        {/* Pagination Widget */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-12 mb-6">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {getPageNumbers(currentPage, totalPages).map((pageNum, idx) => {
              if (pageNum === "...") {
                return (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-2 text-neutral-400 dark:text-neutral-500 font-medium select-none"
                  >
                    ...
                  </span>
                );
              }
              const isActive = pageNum === currentPage;
              return (
                <button
                  key={`page-${pageNum}`}
                  onClick={() => handlePageChange(pageNum as number)}
                  className={`h-10 w-10 flex items-center justify-center rounded-lg font-semibold text-sm transition-all ${
                    isActive
                      ? "bg-[#FFB51D] text-[#131222] shadow-sm cursor-default"
                      : "border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
      <IKFooter />
    </SimplePageBackground>
  );
}
